import React, { createContext, useContext, useState, useEffect } from 'react';
import { getUser, updateUser } from '../api/userDataService';
import { useVisualMessage } from '../components/Validation/VisualMessageContext';

const TooltipContext = createContext();

export const useTooltips = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltips must be used within a TooltipProvider');
  }
  return context;
};

export const TooltipProvider = ({ children }) => {
  // tooltipsEnabled is ONLY stored in database, NOT in localStorage
  // Initial state is null until we fetch from database
  const [tooltipsEnabled, setTooltipsEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTooltipsOnLogin, setShowTooltipsOnLogin] = useState(false);
  const { showSuccess, showInfo, showError } = useVisualMessage();
  
  // Initialize shown pages from localStorage
  const [tooltipsShownForPages, setTooltipsShownForPages] = useState(() => {
    const stored = localStorage.getItem('tooltipsShownForPages');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  useEffect(() => {
    const fetchTooltipPreference = async () => {
      // Check if user is authenticated before trying to fetch preferences
      const token = localStorage.getItem('token');
      if (!token) {
        // User is not authenticated, set to null and stop loading
        setTooltipsEnabled(null);
        setLoading(false);
        setShowTooltipsOnLogin(false);
        return;
      }

      try {
        // ALWAYS fetch from database - NEVER use localStorage for tooltipsEnabled
        const response = await getUser();
        if (response?.result?.user) {
          const user = response.result.user;
          // Read tooltipsEnabled ONLY from database
          let enabled;
          if (user.tooltipsEnabled !== undefined) {
            // User has preference in database - use it
            enabled = user.tooltipsEnabled;
          } else {
            // New user - default to true and save to database
            enabled = true;
            try {
              await updateUser({ tooltipsEnabled: true });
            } catch (error) {
              console.error('TooltipContext: Failed to save tooltipsEnabled to database:', error);
              // If save fails, still use true as default but log the error
            }
          }
          // Set state from database value ONLY
          setTooltipsEnabled(enabled);
          
          
          // If tooltips are disabled in backend (permanent disable), don't show them
          if (!enabled) {
            setShowTooltipsOnLogin(false);
            // Consume any armed tour flag so it can't fire later if re-enabled.
            localStorage.removeItem('pendingTooltipTour');
            // Mark welcome popup as seen so it doesn't show when tooltips are disabled
            sessionStorage.setItem('hasSeenWelcomePopup', 'true');
            localStorage.setItem('hasSeenWelcomePopup', 'true');
            return;
          }
          
          // Check for manual trigger first (takes priority)
          const manualTrigger = localStorage.getItem('manualTooltipTrigger');
          // `pendingTooltipTour` is set ONLY by a genuine login (login form / SSO)
          // in App.js — NOT on a returning-user reload. This is what makes the
          // tour show once per login instead of on every homepage/page load.
          const loginPending = localStorage.getItem('pendingTooltipTour') === 'true';
          if (enabled && manualTrigger === 'true') {
            setShowTooltipsOnLogin(true);
            // Don't remove manualTooltipTrigger here - let TooltipTourOverlay handle it
            // when all pages have been shown. This allows it to persist across navigations.
            // Clear shown pages so they show again
            setTooltipsShownForPages(new Set());
            localStorage.removeItem('tooltipsShownForPages');
            // Clear session flag so tour can start
            localStorage.removeItem('tourTakenThisSession');
          } else if (enabled && loginPending) {
            // Genuine new login: show welcome popup and tooltips once.
            // Consume the one-shot flag so subsequent reloads within this login
            // (which also re-run this fetch / re-fire the userLogin event) do NOT
            // re-show the tour.
            localStorage.removeItem('pendingTooltipTour');

            // This replicates the "View Tour" button behavior - show welcome popup first, then tooltips.
            // Clear the session flag on login so tooltips can show again for this new session
            const tourTakenThisSession = localStorage.getItem('tourTakenThisSession') === 'true';
            if (tourTakenThisSession) {
              // Tour was taken in a previous session - clear it for this new login
              localStorage.removeItem('tourTakenThisSession');
            }

            // Clear shown pages so tooltips can show again on this login
            setTooltipsShownForPages(new Set());
            localStorage.removeItem('tooltipsShownForPages');

            // Clear welcome popup flag so it shows again on login (like "View Tour" button)
            // Clear both sessionStorage and localStorage to ensure it shows
            sessionStorage.removeItem('hasSeenWelcomePopup');
            localStorage.removeItem('hasSeenWelcomePopup');

            // Clear welcome tour flags so they start fresh
            localStorage.removeItem('welcomeTourStarted');
            localStorage.removeItem('tooltipAudioEnabled');
            // Also clear saved tour state on login
            sessionStorage.removeItem('tooltipTourState');

            // Set showTooltipsOnLogin so tooltips will show after welcome popup is clicked
            setShowTooltipsOnLogin(true);
          }
          // else (enabled, no manual trigger, no pending login): this is a reload
          // or returning-user load — leave the already-shown state intact and do
          // NOT re-show the tour.
        }
      } catch (error) {
        console.error('Error fetching tooltip preference from database:', error);
        // If we can't fetch from database, set to null (don't assume a value)
        setTooltipsEnabled(null);
        setShowTooltipsOnLogin(false);
      } finally {
        setLoading(false);
      }
    };

    fetchTooltipPreference();
    
    // Listen for custom login event (dispatched from App.js when user logs in)
    const handleLogin = () => {
      // Add a small delay to ensure token is fully set
      setTimeout(() => {
        fetchTooltipPreference();
      }, 100);
    };
    
    window.addEventListener('userLogin', handleLogin);
    
    return () => {
      window.removeEventListener('userLogin', handleLogin);
    };
  }, []);

  const toggleTooltips = async (enabled) => {
    // Check if user is authenticated before trying to update
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('TooltipContext: Cannot update tooltipsEnabled - user not authenticated');
      return;
    }

    // Update local state IMMEDIATELY for responsive UI
    setTooltipsEnabled(enabled);
    
    try {
      // SAVE TO DATABASE - update happens in background
      await updateUser({
        tooltipsEnabled: enabled
      });
      
        
      // Show success notification
      if (enabled) {
        showSuccess('Tooltip tour enabled', 3000);
        // Clear welcome popup flag when re-enabling so it can show again
        sessionStorage.removeItem('hasSeenWelcomePopup');
        localStorage.removeItem('hasSeenWelcomePopup');
        // Also clear tour flags so tour can start fresh
        localStorage.removeItem('tourTakenThisSession');
        localStorage.removeItem('tooltipsShownForPages');
      } else {
        showInfo('Tooltip tour disabled', 3000);
        // Mark welcome popup as seen when disabling so it doesn't show
        sessionStorage.setItem('hasSeenWelcomePopup', 'true');
        localStorage.setItem('hasSeenWelcomePopup', 'true');
        // Clear any tour flags
        localStorage.removeItem('welcomeTourStarted');
        localStorage.removeItem('tooltipAudioEnabled');
        setShowTooltipsOnLogin(false);
      }
    } catch (error) {
      console.error('Error updating tooltip preference:', error);
      // Revert on error
      setTooltipsEnabled(!enabled);
      // Show error notification
      showError('Failed to update welcome tour setting', 3000);
    }
  };

  const clearShowTooltipsOnLogin = () => {
    setShowTooltipsOnLogin(false);
    // Clear dismissed tooltips from session storage when user navigates away
    // This allows tooltips to show again on next login
  };

  const triggerTooltips = () => {
    if (tooltipsEnabled) {
      setShowTooltipsOnLogin(true);
      // Arm the one-shot flag so the tour survives the full-page reload that the
      // Settings "View Tour" button performs (React state alone would be lost).
      localStorage.setItem('pendingTooltipTour', 'true');
      // Clear welcome popup flag so it shows again (both session and persistent)
      sessionStorage.removeItem('hasSeenWelcomePopup');
      localStorage.removeItem('hasSeenWelcomePopup');
      // Clear welcome tour flags so they start fresh
      localStorage.removeItem('welcomeTourStarted');
      localStorage.removeItem('tooltipAudioEnabled');
      // Clear shown pages so tooltips show again on all pages
      setTooltipsShownForPages(new Set());
      localStorage.removeItem('tooltipsShownForPages');
      // Clear session flag so tour can start
      localStorage.removeItem('tourTakenThisSession');
      // Clear lastLoginTime so it can trigger again
      localStorage.removeItem('lastLoginTime');
      // DON'T set manualTooltipTrigger - we want WelcomePopup to show first, then tooltips start when user clicks "Take Tour"
    }
  };

  // Mark a specific page as having shown tooltips
  // Pages are tracked independently - showing tooltips on 'profile' does not affect 'cv' and vice versa
  // Both pages are controlled by the same tooltipsEnabled setting
  const markPageAsShown = (page) => {
    const newSet = new Set(tooltipsShownForPages);
    newSet.add(page);
    setTooltipsShownForPages(newSet);
    localStorage.setItem('tooltipsShownForPages', JSON.stringify(Array.from(newSet)));
  };

  // Check if tooltips have been shown for a specific page
  // Returns true only if THIS specific page has been shown
  // Each page (profile, cv) is tracked independently
  const hasShownTooltipsForPage = (page) => {
    return tooltipsShownForPages.has(page);
  };
  
  // Don't auto-clear showTooltipsOnLogin - let it persist until page is marked as shown
  // The overlay will mark the page as shown when tour completes or is dismissed

  const value = {
    tooltipsEnabled,
    toggleTooltips,
    showTooltipsOnLogin,
    clearShowTooltipsOnLogin,
    triggerTooltips,
    markPageAsShown,
    hasShownTooltipsForPage,
    loading
  };

  return (
    <TooltipContext.Provider value={value}>
      {children}
    </TooltipContext.Provider>
  );
};

